type TimePoint = { x: number; y: number };

/**
 * A series as points, `x` in unix ms: what the chart, its legend and its export draw, and how a
 * chart saved in a conversation before columns still arrives.
 */
export type PointTimeSerie = { label: string; data: TimePoint[]; isLineDashed?: boolean };

/** A series of a bucketed read: `y[i]` is at `steppedStart + i × interval`, null where nothing reported. */
export type SteppedTimeSerie = { label: string; y: (number | null)[]; isLineDashed?: boolean };

/** A series of a raw read: no common step, so `y[i]` was sampled at its own `x[i]`. */
export type RawTimeSerie = { label: string; x: number[]; y: number[]; isLineDashed?: boolean };

export type LineChartBase = {
  title: string;
  yAxisTitle?: string;
  yAxisType?: 'default' | 'percentage';
  yUnitRange?: { threshold: number; label: string }[];
  yAxisScale?: 'linear' | 'log';
  startTime?: number | null;
  endTime?: number | null;
};

export type SteppedLineChartData = LineChartBase & {
  timestamps: 'stepped';
  steppedStart: number;
  interval: number;
  series: SteppedTimeSerie[];
};

export type RawLineChartData = LineChartBase & { timestamps: 'raw'; series: RawTimeSerie[] };

/** A chart saved in a conversation before columns: no `timestamps`, points instead. */
export type PointLineChartData = LineChartBase & {
  timestamps?: undefined;
  interval?: number | null;
  series: PointTimeSerie[];
};

export type LineChartData = SteppedLineChartData | RawLineChartData | PointLineChartData;

/** A null value is no point at all: core-ui's gap filler then breaks the line there. */
const steppedToPoints = (serie: SteppedTimeSerie, steppedStart: number, interval: number): PointTimeSerie => ({
  label: serie.label,
  isLineDashed: serie.isLineDashed,
  data: serie.y.flatMap((value, index) =>
    value == null ? [] : [{ x: steppedStart + index * interval * 1000, y: value }],
  ),
});

/** Up to the shorter column: the schema cannot require `x` and `y` to have the same length. */
const rawToPoints = (serie: RawTimeSerie): PointTimeSerie => ({
  label: serie.label,
  isLineDashed: serie.isLineDashed,
  data: serie.y.slice(0, serie.x.length).map((value, index) => ({ x: serie.x[index], y: value })),
});

export const toPointTimeSeries = (chart: LineChartData): PointTimeSerie[] => {
  switch (chart.timestamps) {
    case 'stepped':
      return chart.series.map((serie) => steppedToPoints(serie, chart.steppedStart, chart.interval));
    case 'raw':
      return chart.series.map(rawToPoints);
    default:
      return chart.series;
  }
};
